// React & Vendor Libs
const { useState, useMemo, useCallback, useEffect } = wp.element;

// NekoUI
import { NekoButton, NekoBlock, NekoWrapper, NekoColumn, NekoInput, NekoTextArea,
  NekoSelect, NekoOption, NekoSettings, NekoTypo, NekoTheme } from '@neko-ui';

import { sortIgnoreItemToEnd } from '../helper';
import useDatabaseCleanerContext, { cleanStyles, countBusyValue, deleteBusyValue } from '../context';

const emptyCustomQuery = {
  name: '',
  clean_style: '',
  query_count: '',
  query_delete: '',
};
const SettingsCustomQueries = () => {

  const { customQueries, busy, busyAction, customQueriesCount, editCustomQueries, clickedTab } = useDatabaseCleanerContext();
  const { toggleModal, setCustomQueriesCount, setEditCustomQueries, updateOption, customQueryDelete,
    refreshCostomQueryCount, allRefreshCostomQueryCount } = useDatabaseCleanerContext();
  const enteredCount = clickedTab.CustomQueries;

  useEffect(() => {
    if (customQueriesCount.length) return;
    if (enteredCount === 1 && customQueries) {
      onAllRefresh(true);
    }
  }, [enteredCount, customQueries]);

  const onRefresh = useCallback(( item, callback ) => {
    const onSuccess = (item, response) => {
      const newCustomQueriesCount = customQueriesCount.filter(v => v.item !== item);
      setCustomQueriesCount([...newCustomQueriesCount, { item, count: response.data }]);
      if (callback) {
        callback(response);
      }
    }
    refreshCostomQueryCount({ item, onSuccess });
  }, [customQueriesCount, setCustomQueriesCount] );

  const onAllRefresh = async (exceptManual = false) => {
    const onSuccess = (newCounts) => {
      setCustomQueriesCount([...newCounts]);
    }
    const editCustomQueryItems = editCustomQueries.map(v => v.item);
    const targetCustomQueries = customQueries.filter(v => !editCustomQueryItems.includes(v.item));
    let counts = [...customQueriesCount];
    const targetCleanStyle = exceptManual ? ['auto'] : ['auto', 'manual'];
    const autoCustomQueries = targetCustomQueries.filter(v => targetCleanStyle.includes(v.clean_style));
    for (const data of autoCustomQueries) {
      counts = await allRefreshCostomQueryCount({ item: data.item, counts, onSuccess });
    }
  };

  const onDeleteAll = useCallback(( item, callback ) => {
    customQueryDelete({ item, signal: undefined, onSuccess: (item, response) => {
      onRefresh(item);
      if (callback) {
        callback(response);
      }
    } });
  }, [onRefresh]);

  const onUpdateCustomQueries = useCallback(( newCustomQueries, item = 'cq-new' ) => {
    updateOption({ busyItem: item, param: { name: 'custom_queries', value: newCustomQueries } });
  }, [updateOption]);

  const onChange = useCallback(({ item, key, value }) => {
    const newCustomQuery = customQueries.find(v => v.item === item);
    newCustomQuery[key] = value;
    const newCustomQueries = [ ...customQueries.filter(v => v.item !== item), newCustomQuery ];
    onUpdateCustomQueries(newCustomQueries, item);
  }, [onUpdateCustomQueries, customQueries]);

  const addCustomQuery = useCallback(() => {
    onUpdateCustomQueries([ ...customQueries, { ...emptyCustomQuery } ]);
  }, [onUpdateCustomQueries, customQueries]);

  const deleteCustomQuery = useCallback((customQuery) => {
    onUpdateCustomQueries(customQueries.filter(v => v.item !== customQuery.item), customQuery.item);
  }, [onUpdateCustomQueries, customQueries]);

  /**
   * JSX
   */
  const editJsx = useCallback((customQuery) => {
    if (!customQuery) return null;
    const canBack = editCustomQueries.some(v => v.item === customQuery.item);
    const isBusy = busyAction.some(b => b.item === customQuery.item);
    return (
      <NekoBlock key={customQuery.item} busy={isBusy} className="primary" style={{ marginTop: 0, backgroundColor: NekoTheme.white, padding: 0 }}>
        <NekoSettings title="Name">
          <NekoInput value={customQuery.name} onBlur={(value) => onChange({ item: customQuery.item, key: 'name', value })} />
        </NekoSettings>
        <NekoSettings title="Clean">
          <div style={{ width: '90px' }}>
            <NekoSelect scrolldown={true} onChange={(value) => onChange({ item: customQuery.item, key: 'clean_style', value })}
              value={customQuery.clean_style} disabled={isBusy}>
              {cleanStyles.map((v) => <NekoOption id={v.value} value={v.value} label={v.label} />)}
            </NekoSelect>
          </div>
        </NekoSettings>
        <NekoSettings title="Query Count">
          <NekoTextArea value={customQuery.query_count} onBlur={(value) => onChange({ item: customQuery.item, key: 'query_count', value })} />
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <NekoButton disabled={!customQuery.query_count} onClick={() => onRefresh(customQuery.item, (response) => alert(`Result: ${response.data}`) )}>Run Count</NekoButton>
          </div>
        </NekoSettings>
        <NekoSettings title="Query Delete">
          <NekoTextArea value={customQuery.query_delete} onBlur={(value) => onChange({ item: customQuery.item, key: 'query_delete', value })} />
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <NekoButton className="danger" disabled={!customQuery.query_delete} onClick={() => onDeleteAll(customQuery.item, (response) => alert(`Result: ${response.data} affected.`))}>Run Delete</NekoButton>
          </div>
        </NekoSettings>
        <NekoSettings contentAlign="right">
          {canBack && <NekoButton onClick={() => { setEditCustomQueries(editCustomQueries.filter(v => v.item != customQuery.item)) }}>Back</NekoButton>}
        </NekoSettings>
      </NekoBlock>
    );
  }, [busyAction, editCustomQueries, setEditCustomQueries]);

  const headerJsx = useMemo(() => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', background: NekoTheme.white, padding: '8px', fontWeight: 'bold' }}>
        <NekoTypo p style={{ margin: 0, width: '55px' }}></NekoTypo>
        <NekoTypo p style={{ margin: 0, width: '35%' }}>Name</NekoTypo>
        <NekoTypo p style={{ margin: 0, width: '20%' }}>Count</NekoTypo>
        <NekoTypo p style={{ margin: 0, width: '20%' }}>Clean</NekoTypo>
        <NekoTypo p style={{ margin: 0, width: '25%' }}>Action</NekoTypo>
      </div>
    );
  }, []);

  const displayJsx = useCallback((customQuery, needsHeader) => {
    if (!customQuery) return null;
    const isIgnore = customQuery.clean_style === 'never';
    const onDeleteClick = () => toggleModal({modal: 'deleteAll', enable: true, onNext: () => onDeleteAll(customQuery.item)});
    const isRefreshBusy = busyAction.some(busy => busy.item === countBusyValue(customQuery.item));
    const count = customQueriesCount.find(c => c.item === customQuery.item)?.count ?? '-';
    const showDeleteButton = count > 0 && !busyAction.some(busy => busy.item === deleteBusyValue(customQuery.item));
    return (
      <>
        {needsHeader && headerJsx}
        <div key={customQuery.item} style={{ display: 'flex', alignItems: 'center', background: NekoTheme.white, padding: '8px' }}>
          <NekoTypo p style={{ margin: 0, width: '55px' }}>
            <NekoButton className="danger" onClick={() => deleteCustomQuery(customQuery)} busyIconSize="100%"
            icon="close" style={{ minWidth: 40, width: 40 }}></NekoButton>
          </NekoTypo>
          <NekoTypo p style={{ margin: 0, width: '35%' }}>{customQuery.name}</NekoTypo>
          <NekoTypo p style={{ margin: 0, width: '20%', position: 'relative' }}>{isRefreshBusy ? '-' : count}</NekoTypo>
          <NekoTypo p style={{ margin: 0, width: '20%' }}>{customQuery.clean_style}</NekoTypo>
          <div style={{ display: 'flex', alignItems: 'center', width: '25%' }}>
            {!isIgnore && <NekoButton isBusy={isRefreshBusy} onClick={() => onRefresh(customQuery.item)} icon="replay" style={{ justifyContent: 'center' }}></NekoButton>}
            {!isIgnore && showDeleteButton && <NekoButton className="danger" onClick={onDeleteClick} icon="trash" style={{ justifyContent: 'center' }}></NekoButton>}
            <NekoButton onClick={() => { setEditCustomQueries([ ...editCustomQueries, { item: customQuery.item } ]) }} icon="pencil" style={{ justifyContent: 'center' }}></NekoButton>
          </div>
        </div>
      </>
    );
  }, [busyAction, editCustomQueries, setEditCustomQueries, customQueriesCount, deleteCustomQuery]);

  const sortedCustomQueries = useMemo(() => sortIgnoreItemToEnd(customQueries), [customQueries]);

  return (
    <NekoWrapper>
      <NekoColumn minimal>

        <div style={{ margin: '-10px -10px 25px -10px', padding: 10, background: '#00000014' }}>
          <NekoButton onClick={() => { addCustomQuery() }} color={NekoTheme.purple} disabled={busy}>
            + Add Custom
          </NekoButton>
        </div>

        <NekoBlock busy={busy} title="Custom Queries" className="primary" action={<NekoButton icon="replay" rounded onClick={() => onAllRefresh()} />}>
          {sortedCustomQueries.map((v, i) => {
            if (!v.name || !v.clean_style || !v.query_count || !v.query_delete) return editJsx(v);
            if (editCustomQueries.some(edit => edit.item === v.item)) {
              return editJsx(v);
            } else {
              const needsHeader = (
                i === 0
                || editCustomQueries.some(edit => edit.item === sortedCustomQueries[i-1].item)
                || (!sortedCustomQueries[i-1].name || !sortedCustomQueries[i-1].clean_style || !sortedCustomQueries[i-1].query_count || !sortedCustomQueries[i-1].query_delete));
              return displayJsx(v, needsHeader);
            }
          })}
        </NekoBlock>

      </NekoColumn>

    </NekoWrapper>
  );
};

export default SettingsCustomQueries;